import { CustomDatePicker, SelectAutocomplete } from '../../../../components';
import { statusLabels, statusOptions } from '../../shedular.utils';

import { FiltersSectionProps } from './FilterSection.types';
import './FiltersSection.css';

/**
 * Filters section component for scheduler page
 * Provides search, date, time, status, user, and edit date filtering capabilities
 *
 * Features:
 * - Text search by stage name
 * - Date and time pickers with Ukrainian localization
 * - Status dropdown filter
 * - User name search
 * - Edit date filtering
 * - Save button showing pending updates count
 *
 * @param props - Component props containing filter state and callbacks
 * @returns JSX element containing the scheduler filters interface
 */
export function FiltersSection({
  filters,
  pendingUpdates,
  hasChanges,
  isLoading,
  onFilterChange,
  onSaveAll,
  onResetAll
}: FiltersSectionProps) {
  return (
    <div className="scheduler-filters-section">
      <div className="scheduler-filters-container">
        <div className="scheduler-filters-grid">
          <div className="scheduler-filter-field">
            <label className="scheduler-field-label">Пошук</label>
            <input
              type="text"
              value={filters.search || ''}
              onChange={e => onFilterChange('search', e.target.value)}
              className="scheduler-filter-input"
              placeholder="Пошук по назві"
            />
          </div>
          <div className="scheduler-filter-field">
            <label className="scheduler-field-label">Дата</label>
            <CustomDatePicker
              value={filters.date ? new Date(filters.date) : null}
              onChange={date => onFilterChange('date', date || null)}
              format="dd.MM.yyyy"
              outputFormat="date"
            />
          </div>
          <div className="scheduler-filter-field">
            <label className="scheduler-field-label">Час</label>
            <CustomDatePicker
              value={filters.time ? new Date(`2000-01-01T${filters.time}`) : null}
              onChange={val => onFilterChange('time', val ? (val as string).split('T')[1] : null)}
              showTime={true}
              timeFormat="HH:mm"
              outputFormat="datetime-local"
            />
          </div>
          <div className="scheduler-filter-field">
            <label className="scheduler-field-label">Статус</label>
            <SelectAutocomplete
              options={statusOptions.map(s => statusLabels[s])}
              value={filters.status ? statusLabels[filters.status] : ''}
              onChange={label => {
                const status = statusOptions.find(s => statusLabels[s] === label) || '';
                onFilterChange('status', status);
              }}
              showClearButton={!!filters.status}
              onClear={() => onFilterChange('status', '')}
              placeholder="Всі"
              selectOnly
            />
          </div>
          <div className="scheduler-filter-field">
            <label className="scheduler-field-label">Користувач</label>
            <input
              type="text"
              value={filters.user || ''}
              onChange={e => onFilterChange('user', e.target.value)}
              className="scheduler-filter-input"
              placeholder="Введіть ім'я користувача"
            />
          </div>
          <div className="scheduler-filter-field">
            <label className="scheduler-field-label">Дата редагування</label>
            <CustomDatePicker
              value={filters.edit_date ? new Date(filters.edit_date) : null}
              onChange={date => onFilterChange('edit_date', date || null)}
              format="dd.MM.yyyy"
              outputFormat="date"
            />
          </div>
        </div>
      </div>

      <div className="scheduler-actions-section">
        {hasChanges && (
          <>
            <button
              onClick={onResetAll}
              className="scheduler-reset-button"
            >
              Скинути зміни
            </button>
            <button
              onClick={onSaveAll}
              disabled={isLoading}
              className={`scheduler-save-button ${isLoading ? 'disabled' : ''}`}
            >
              Зберегти ({pendingUpdates.length})
            </button>
          </>
        )}
      </div>
    </div>
  );
}
